/* In-browser face recognition for device-local worker matching.
   The dashboard contract no longer matches photos server-side: the device
   downloads each worker's enrollment photo, computes a face descriptor
   locally (@vladmandic/face-api, models served from /models) and matches
   the live camera frame against the cached descriptors. */
import * as faceapi from "@vladmandic/face-api";

const MODEL_URL = `${process.env.PUBLIC_URL || ""}/models`;

let loadP = null;
export function loadModels() {
  if (!loadP) {
    loadP = Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
      faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
      faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
    ]).catch((e) => { loadP = null; throw e; });
  }
  return loadP;
}

/* Bump when detector settings change so cached "no face" outcomes are retried
   exactly once against the new settings (see roster sync). */
export const DETECTOR_VERSION = 2;

/* Fast pass first (live video every ~400ms), then a slower/more lenient pass —
   larger input + lower score threshold — so small or off-centre faces in
   enrollment photos and poor kiosk lighting still get detected. */
const DET_PASSES = [
  new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.4 }),
  new faceapi.TinyFaceDetectorOptions({ inputSize: 512, scoreThreshold: 0.25 }),
];

/* 128-d descriptor from a live <video>/<img>/canvas — null when no face found. */
export async function descriptorFrom(media, { thorough = false } = {}) {
  await loadModels();
  for (const opts of (thorough ? DET_PASSES : DET_PASSES.slice(0, 1))) {
    const det = await faceapi.detectSingleFace(media, opts).withFaceLandmarks().withFaceDescriptor();
    if (det) return det.descriptor;
  }
  return null;
}

/* Descriptor from an enrollment photo blob (roster sync). */
export async function descriptorFromBlob(blob) {
  await loadModels();
  const img = await faceapi.bufferToImage(blob);
  try {
    return await descriptorFrom(img, { thorough: true });
  } finally {
    try { URL.revokeObjectURL(img.src); } catch (_) {}
  }
}

export const distance = (a, b) => faceapi.euclideanDistance(a, b);

/* Euclidean distance below this counts as the same person. 0.6 is the
   face-api convention; 0.5 proved too strict against enrollment photos taken
   in different lighting than the kiosk camera. */
export const MATCH_THRESHOLD = 0.6;
